import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { SidePanelPropsTable } from '@/components/panels/instance/tabs/sidePanelPropsTable';
import { CircleImage } from '@/components/shared/circleImage';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { ApplicationsLogosWithAliases } from '@/data/inventory/data';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { restoreMongoAtlasState } from '../restoreMongoAtlasWizard';

export const MongoAtlasConfiguration = (
  props: StepProps<restoreMongoAtlasState>
) => {
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const { data: inventoryResourceBody } = dal.inventory.get(
    props.wizardState.resourceId
  );
  const { data: projectsResponse } = dal.restore.atlasProjects.list(
    props.wizardState.restoreAccount!.id
  );

  useEffect(() => {
    if (!props.wizardState.projectId && projectsResponse?.projects) {
      props.setWizardState((state) => ({
        ...state,
        projectId: projectsResponse.projects[0]?.id,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsResponse]);

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={() => {
        if (
          isRestoring ||
          !props.wizardState.projectId ||
          !props.wizardState.resourceName
        ) {
          return false;
        }

        return true;
      }}
      onNextClick={async () => {
        setIsRestoring(true);
        setError(undefined);
        const tags = props.wizardState.tags || {};
        tags['eon:restore'] = 'true';

        return dal.restore.atlasProjects
          .restore(
            props.wizardState.resourceId,
            props.wizardState.snapshot.id,
            {
              tags,
              restoreAccountId: props.wizardState.restoreAccount!.id,
              clusterName: props.wizardState.resourceName!,
              atlasProjectId: props.wizardState.projectId!,
            }
          )
          .then(() =>
            props.finish(
              `Started restore job for ${props.wizardState.resourceName}`
            )
          )
          .catch((e) => {
            setIsRestoring(false);
            setError(`Failed to restore cluster: ${e.message}`);
          });
      }}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mb-[24px] px-[40px]'>
        <Box className='w-full flex justify-between mt-[20px] gap-[36px]'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>Project</FormLabel>
            <Select
              size='small'
              value={
                (projectsResponse?.projects && props.wizardState.projectId) ||
                ''
              }
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  projectId: event.target.value,
                }))
              }
            >
              {(projectsResponse?.projects || []).map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FormControl size='small' className='w-full mt-[20px]'>
          <FormLabel>Database name</FormLabel>
          <TextField
            size='small'
            value={props.wizardState.resourceName || ''}
            onChange={(event) =>
              props.setWizardState((state) => ({
                ...state,
                resourceName: event.target.value,
              }))
            }
          />
        </FormControl>
      </Box>
      <Divider />
      {inventoryResourceBody?.resource.resourceProperties?.awsRds?.engine && (
        <>
          <Box className='w-full my-[24px]'>
            <Typography className='px-[40px]' variant='h6'>
              Database settings
            </Typography>
            <SidePanelPropsTable className='my-[24px]'>
              <tbody>
                <tr>
                  <td className='props-col'>Engine</td>
                  <td className='capitalize'>
                    <div className='flex items-center'>
                      <CircleImage
                        alt={
                          inventoryResourceBody.resource.resourceProperties
                            .awsRds.engine
                        }
                        src={
                          ApplicationsLogosWithAliases[
                            inventoryResourceBody.resource.classifications
                              ?.appsDetails?.apps?.[0]?.name ||
                              inventoryResourceBody.resource.resourceProperties
                                ?.awsRds.engine ||
                              ''
                          ]
                        }
                        className='mr-[8px]'
                      />
                      <Typography color='var(--mui-palette-text-secondary)'>
                        {
                          inventoryResourceBody.resource.resourceProperties
                            .awsRds.engine
                        }
                      </Typography>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className='props-col'>Engine version</td>
                  <td>
                    {
                      inventoryResourceBody.resource.resourceProperties.awsRds
                        .engineVersion
                    }
                  </td>
                </tr>
              </tbody>
            </SidePanelPropsTable>
          </Box>
          <Divider />
        </>
      )}
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags || {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
